import { useState, useEffect } from 'react'

interface CurrencyInfo {
  price: string
  currency: string
  loading: boolean
}

const BASE_PRICE_ARS = 9990

// Approximate conversion rates from ARS (updated periodically)
const CURRENCY_MAP: Record<
  string,
  { symbol: string; code: string; rate: number }
> = {
  AR: { symbol: '$', code: 'ARS', rate: 1 },
  US: { symbol: 'US$', code: 'USD', rate: 0.00085 },
  MX: { symbol: 'MX$', code: 'MXN', rate: 0.0145 },
  CO: { symbol: 'COP$', code: 'COP', rate: 3.4 },
  CL: { symbol: 'CLP$', code: 'CLP', rate: 0.78 },
  PE: { symbol: 'S/', code: 'PEN', rate: 0.0032 },
  UY: { symbol: 'UY$', code: 'UYU', rate: 0.034 },
  BR: { symbol: 'R$', code: 'BRL', rate: 0.0048 },
  ES: { symbol: '€', code: 'EUR', rate: 0.00078 },
  EC: { symbol: 'US$', code: 'USD', rate: 0.00085 },
  BO: { symbol: 'Bs', code: 'BOB', rate: 0.0059 },
  PY: { symbol: '₲', code: 'PYG', rate: 6.3 },
  VE: { symbol: 'US$', code: 'USD', rate: 0.00085 },
  // European countries
  DE: { symbol: '€', code: 'EUR', rate: 0.00078 },
  FR: { symbol: '€', code: 'EUR', rate: 0.00078 },
  IT: { symbol: '€', code: 'EUR', rate: 0.00078 },
  GB: { symbol: '£', code: 'GBP', rate: 0.00067 },
}

function formatPrice(amount: number, code: string): string {
  // For currencies that typically show no decimals
  const noDecimals = ['ARS', 'CLP', 'COP', 'PYG']
  if (noDecimals.includes(code)) {
    return amount.toLocaleString('es-AR', { maximumFractionDigits: 0 })
  }
  return amount.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function useCurrency(): CurrencyInfo {
  const [currency, setCurrency] = useState<CurrencyInfo>({
    price: '9.990',
    currency: 'ARS',
    loading: true,
  })

  useEffect(() => {
    let cancelled = false

    async function detectCurrency() {
      try {
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000),
        })
        const data = await response.json()
        const countryCode = data?.country_code || 'AR'

        if (cancelled) return

        const currencyInfo = CURRENCY_MAP[countryCode] || CURRENCY_MAP['AR']
        const convertedPrice = BASE_PRICE_ARS * currencyInfo.rate
        const formattedPrice = formatPrice(convertedPrice, currencyInfo.code)

        setCurrency({
          price: formattedPrice,
          currency: currencyInfo.code,
          loading: false,
        })
      } catch {
        if (!cancelled) {
          setCurrency({
            price: '9.990',
            currency: 'ARS',
            loading: false,
          })
        }
      }
    }

    detectCurrency()
    return () => {
      cancelled = true
    }
  }, [])

  return currency
}
